import React from 'react';

import { Row, Col } from 'antd';
import Axios from 'axios';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { datePickerToApi, getNow } from '@libs/NwMoment';
import { getMoment } from '@libs/NwMoment';
import { getProjectDescriptionsOptions, getProjectTitlesOptions } from '@libs/ProjectUtils';

import { useProjectContext } from '@contexts/ProjectContext';
import { useStateValue } from '@contexts/StateProvider';

import { forceReloadProject, onGetOrderedEventList } from '@hooks/API/Project/UseProject';
import { forceReloadProjectAccountingDetails } from '@hooks/API/Project/UseProjectAccountingDetails';
import { forceReloadProjectStatements } from '@hooks/API/Project/UseProjectStatements';
import { forceReloadProjectTransactions } from '@hooks/API/Project/UseProjectTransactions';
import useTransactionLabels from '@hooks/CachedLists/UseTransactionLabels';
import useConfigurations from '@hooks/Configurations/UseConfigurations';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import { Loading } from '@components/Gui/Loading';
import {
    NWTextAreaAutoComplete,
    NWAutoComplete,
    NWContactWithBillingInfo,
    NWBookingEventSelectorForTransactions,
    NWDatePicker,
} from '@components/Gui/NwForm/NwFormItems';
import { NwForm } from '@components/Gui/NwForm/NwFormWrapper';
import { ProjectTransactionFormContainer } from '@components/Project/ProjectTransactions/Transactions/ProjectTransactionsUI';
import {
    getSingleConfirmedJobEvent,
    handleChangeEventForDate,
} from '@components/Project/ProjectTransactions/Transactions/TransactionCreateFormUtils';

import AmountLine from './Common/AmountLine';
import NwFixedCancelAndSaveBar from '@components/Gui/NwFixedCancelAndSaveBar';
import ServiceLine from './Common/ServiceLine';
import TransactionLabelsSelector from './Common/TransactionLabelsSelector';

const ProductForm = ({ onCloseDrawer, clientBillingInfos, contacts, width, minWidth }) => {
    //onChangeForm

    const transactionProperties = useTransactionLabels('Product');
    const getTransactionLabel = (transactionLabelID) =>
        transactionProperties.labels.find((label) => label.ID === transactionLabelID);
    const defaultTypeQuantity = transactionProperties.startLabel.TypeQuantityName
        ? transactionProperties.startLabel.TypeQuantityName
        : 'Units';

    const breakpoints = useNwBreakPoints();
    const configurations = useConfigurations();

    const contextData = useProjectContext();
    const { projectData: currentProject, transactionsGrouped } = contextData;
    const transactionTitles = [
        ...new Set(
            transactionsGrouped.flatMap((group) => group.Dates.flatMap((date) => date.Transactions.map((t) => t.Title)))
        ),
    ].filter(Boolean);

    const transactionDescriptions = [
        ...new Set(
            transactionsGrouped.flatMap((group) =>
                group.Dates.flatMap((date) => date.Transactions.map((t) => t.Description))
            )
        ),
    ].filter(Boolean);

    const [{ areaContext }] = useStateValue();
    const events = onGetOrderedEventList(currentProject, areaContext, false, false);
    // Se il progetto ha un singolo evento job/ext e quello e' confermato,
    // pre-popoliamo il selettore evento e i campi correlati.
    const preselectedEvent = getSingleConfirmedJobEvent(events);

    const saveTransaction = async (values, setSubmitting) => {
        //TODO: validazione: se c'è evento ci deve essere modella

        let apiData = {
            ProjectID: currentProject.ID,
            ClientID: values.ContactToBill.ContactID,
            ClientBillingInfoID: values.ContactToBill.ContactBillingInfoID,
            Family: 'Product',
            TransactionLabelID: values.TransactionLabelID,
            Title: values.Title,
            SelectedEvent: null,
            Description: values.Description,
            Quantity: values.Quantity[0],
            TypeQuantity: values.Quantity[1],
            Currency: values.Amount[0],
            SingleFee: values.Amount[1],
            RefDate: values.RefDate && datePickerToApi(values.RefDate),
        };
        if (values.ServiceID) {
            apiData.ServiceID = values.ServiceID;
            if (values.AgencyCost) {
                apiData.AgencyCost = values.AgencyCost[1];
            } else {
                apiData.AgencyCost = values.SingleFee[1];
            }
        }
        if (values.SelectedEvent) {
            apiData.EventID = values.SelectedEvent.ID;
        }

        try {
            await Axios.post('/transactions/Product', apiData);
            forceReloadProject(currentProject.ID);
            forceReloadProjectAccountingDetails(currentProject.ID);
            forceReloadProjectTransactions(currentProject.ID);
            forceReloadProjectStatements(currentProject.ID);
            onCloseDrawer();
            setSubmitting(false);
        } catch (error) {
            console.error('Create Product Error => ', error);
            setSubmitting(false);
        }
    };

    const clientBillingInfoDefault = clientBillingInfos.find((item) => item.isDefault);
    let clientBillingInfoDefaultID;
    if (clientBillingInfoDefault) {
        clientBillingInfoDefaultID = clientBillingInfoDefault.ID;
    } else {
        if (clientBillingInfoDefault && clientBillingInfoDefault.length > 0) {
            clientBillingInfoDefaultID = clientBillingInfoDefault[0].ID;
        }
    }

    const initialValues = {
        ContactToBill: { ContactID: currentProject.CustomerID, ContactBillingInfoID: clientBillingInfoDefaultID },
        TransactionLabelID: transactionProperties.startLabel.ID,
        Title: preselectedEvent
            ? `${transactionProperties.startLabel.Label} for ${preselectedEvent.Name}`
            : transactionProperties.startLabel.Label,
        Description: '',
        SelectedEvent: preselectedEvent,
        Amount: [configurations.defaultCurrency, null],
        Quantity: [1, defaultTypeQuantity],
        ServiceID: null,
        RefDate: preselectedEvent
            ? getMoment(preselectedEvent.FirstDate)
            : currentProject.FirstDate
              ? getMoment(currentProject.FirstDate)
              : getNow(),
        AgencyCost: [configurations.defaultCurrency, 0],
    };

    return currentProject ? (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
                Title: Yup.string().required('Title is required'),
                TransactionLabelID: Yup.string().required('Label is required'),
                ContactToBill: Yup.string().required('Client is required'),
            })}
            onSubmit={(values, { setSubmitting }) => {
                setSubmitting(true);
                saveTransaction(values, setSubmitting);
            }}
        >
            {({ isSubmitting, values, setFieldValue, handleSubmit, touched, isValid }) => (
                <NwForm
                    values={values}
                    //onTouchForm={onChangeForm}
                    onFinish={handleSubmit}
                    layout="vertical"
                >
                    <ProjectTransactionFormContainer>
                        <Row gutter={32}>
                            <Col span={24}>
                                <Field
                                    component={NWBookingEventSelectorForTransactions}
                                    label="Event"
                                    name="SelectedEvent"
                                    events={events}
                                    eventSelectedUniqID={preselectedEvent ? preselectedEvent.uniqID : undefined}
                                    onAfterChange={(value) => {
                                        if (value) {
                                            const labelSelected = values.TransactionLabelID;
                                            const transactionLabel = getTransactionLabel(labelSelected);
                                            setFieldValue('Title', `${transactionLabel.Label} for ${value.Name}`);
                                            handleChangeEventForDate(value, setFieldValue);
                                        }
                                    }}
                                />
                                <TransactionLabelsSelector
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    transactionProperties={transactionProperties}
                                    updateTitle
                                />
                                <Field
                                    component={NWContactWithBillingInfo}
                                    fullSelector
                                    value={values.ContactToBill}
                                    label="Client"
                                    name="ContactToBill"
                                    contacts={contacts}
                                    billingInfos={clientBillingInfos}
                                    disabled={contacts.length === 1}
                                    required
                                />
                                <Field
                                    component={NWDatePicker}
                                    label="Date"
                                    name="RefDate"
                                    format="MMM DD, YYYY"
                                    allowClear={false}
                                    value={values.RefDate}
                                />
                                <Field
                                    component={NWAutoComplete}
                                    label="Title"
                                    name="Title"
                                    value={values.Title}
                                    maxLength={255}
                                    options={[
                                        ...(transactionTitles.length > 0
                                            ? [
                                                  {
                                                      label: 'Other Transactions',
                                                      options: transactionTitles.map((title) => ({
                                                          label: title,
                                                          value: title,
                                                      })),
                                                  },
                                              ]
                                            : []),
                                        ...getProjectTitlesOptions(currentProject),
                                    ]}
                                />
                                <Field
                                    component={NWTextAreaAutoComplete}
                                    label="Description"
                                    name="Description"
                                    type="text"
                                    value={values.Description}
                                    maxLength={2500}
                                    autoSize={{ minRows: 4, maxRows: 10 }}
                                    options={[
                                        ...(transactionDescriptions.length > 0
                                            ? [
                                                  {
                                                      label: 'Other Transactions',
                                                      options: transactionDescriptions.map((description) => ({
                                                          label: description,
                                                          value: description,
                                                      })),
                                                  },
                                              ]
                                            : []),
                                        ...getProjectDescriptionsOptions(currentProject),
                                    ]}
                                />
                                <AmountLine
                                    allowZero={true}
                                    values={values}
                                    touched={touched}
                                    onChangeAmount={(value) => {
                                        if (!touched.AgencyCost) {
                                            setFieldValue('AgencyCost', [value[0], value[1]]);
                                        }
                                    }}
                                    amountLabel="Price"
                                />
                                <ServiceLine values={values} setFieldValue={setFieldValue} hasAgencyCost />
                            </Col>
                        </Row>
                    </ProjectTransactionFormContainer>
                    <NwFixedCancelAndSaveBar
                        isSubmitting={isSubmitting}
                        isValid={isValid}
                        onCancel={onCloseDrawer}
                        width={width}
                        minWidth={minWidth}
                    />
                </NwForm>
            )}
        </Formik>
    ) : (
        <Loading />
    );
};

export default ProductForm;
